import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { trans } from '@spotahome/bff/client';
import { Heading } from '@spotahome/ui-library';
import '@spotahome/ui-library/lib/components/Heading.css';

import AlertShape from '@alerts/shapes/alert';
import { getCityName } from '@utils/cities';
import AlertModalContainer from '@components/AlertModalContainer';

import CityAutocompleter from './CityAutocompleter';
import AlertDatePicker from './AlertDatePicker';
import AlertPriceRange from './AlertPriceRange';
import AlertPropertyTypes from './AlertPropertyTypes';
import './AlertModal.scss';

const INITIAL_MEDIUMS = ['email'];

const getInitialState = (alert, cities) => {
  const { cityId } = alert;
  const selectedCity = cities[cityId] || {};

  return {
    cityId,
    errors: {},
    hasError: false,
    currencyIsoCode: selectedCity.currencyIsoCode,
    moveIn: alert.moveIn,
    moveOut: alert.moveOut,
    minPrice: alert.minPrice,
    maxPrice: alert.maxPrice,
    propertyTypes: alert.propertyTypes,
    mediums: INITIAL_MEDIUMS
  };
};

const gatherDataToSend = ({
  cityId,
  moveIn,
  moveOut,
  minPrice,
  maxPrice,
  propertyTypes,
  mediums
}) => ({ cityId, moveIn, moveOut, minPrice, maxPrice, propertyTypes, mediums });

const CloseModalButton = props => {
  return (
    <button
      className="close-button__button"
      type="button"
      onClick={props.onClose}
    >
      x
    </button>
  );
};

class AlertModal extends PureComponent {
  static propTypes = {
    isOpen: PropTypes.bool.isRequired,
    cancel: PropTypes.func.isRequired,
    confirm: PropTypes.func.isRequired,
    cities: PropTypes.shape({
      [PropTypes.string]: PropTypes.shape({
        name: PropTypes.string,
        currencyIsoCode: PropTypes.string
      })
    }).isRequired,
    alert: PropTypes.shape(AlertShape),
    isLoading: PropTypes.bool.isRequired,
    cityNames: PropTypes.arrayOf(PropTypes.string).isRequired
  };

  static defaultProps = {
    alert: {
      cityId: '',
      minPrice: '',
      maxPrice: '',
      propertyTypes: [],
      mediums: INITIAL_MEDIUMS
    }
  };

  state = getInitialState(this.props.alert, this.props.cities);

  componentWillReceiveProps = nextProps => {
    this.value = getCityName(nextProps.alert.cityId);
    this.setState(getInitialState(nextProps.alert, nextProps.cities));
  };

  setCity = (newCityData, cityName) => {
    this.value = cityName;
    this.setState({ ...newCityData }, this.checkErrors);
  };

  setDates = newDates => {
    this.setState(newDates);
  };

  setPrices = newPrices => {
    this.setState(newPrices, this.checkErrors);
  };

  setPropertyType = propertyTypes => {
    this.setState({ propertyTypes });
  };

  getErrors = () => ({
    autocompleter: !!this.hasErrorCity(),
    priceRange: !!this.hasErrorPriceRange()
  });

  checkErrors = () => {
    const errors = this.getErrors();
    const hasError = Object.keys(errors).filter(key => errors[key]).length;

    this.setState({ hasError: !!hasError, errors });

    return hasError;
  };

  hasErrorCity = () =>
    !this.state.cityId || !this.props.cityNames.includes(this.value);

  hasErrorPriceRange = () =>
    this.state.minPrice &&
    this.state.maxPrice &&
    this.state.minPrice > this.state.maxPrice;

  handleConfirm = () => {
    const hasError = this.checkErrors();

    if (!hasError) {
      this.props.confirm(gatherDataToSend(this.state));
    }
  };

  handleChange = value => {
    this.value = value;
  };

  render() {
    const {
      cityId,
      hasError,
      moveIn,
      moveOut,
      minPrice,
      maxPrice,
      propertyTypes,
      currencyIsoCode,
      errors
    } = this.state;

    return (
      <AlertModalContainer
        isOpen={this.props.isOpen}
        cancel={this.props.cancel}
        accept={this.handleConfirm}
        hasError={hasError}
        isLoading={this.props.isLoading}
      >
        <div className="add-edit-form__header">
          <Heading
            level={4}
            theme="dark"
            size="medium"
            className="add-edit-form__title"
            text={trans('alerts.modal.title')}
          />
          <CloseModalButton onClose={this.props.cancel} />
        </div>
        <div className="add-edit-form__grid">
          <CityAutocompleter
            cities={this.props.cities}
            cityName={this.value}
            cityId={cityId}
            cityNames={this.props.cityNames}
            onSelectedElement={this.setCity}
            onChange={this.handleChange}
            hasError={errors.autocompleter}
          />
          <AlertDatePicker
            onChange={this.setDates}
            moveIn={moveIn}
            moveOut={moveOut}
          />
          <AlertPriceRange
            minPrice={minPrice}
            maxPrice={maxPrice}
            currencyIsoCode={currencyIsoCode}
            onChange={this.setPrices}
            hasError={errors.priceRange}
          />
          <AlertPropertyTypes
            propertyTypes={propertyTypes}
            onChange={this.setPropertyType}
          />
        </div>
      </AlertModalContainer>
    );
  }
}

export default AlertModal;
